import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SupportStaff from "./SupportStaff";
import SupportService from "./SupportService";
import "../assets/css/SupportContacts.css";
import "../assets/css/DigixLeads.css";

function SupportContacts({
  supportContacts = [],
  supportStaff = [],
  onRefresh,
  onAddContact,
  onViewContact,
  onEditContact,
  onDeleteContact,
  initialActiveTab = "Service",
  onTabChange
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path === "/support-contacts/service" || path.startsWith("/support-contacts/service/")) return "Service";
    return initialActiveTab || "Service";
  });
  const [localStaff, setLocalStaff] = useState(supportStaff);
  const [localContacts, setLocalContacts] = useState(supportContacts);

  useEffect(() => {
    setLocalStaff(supportStaff);
  }, [supportStaff]);

  useEffect(() => {
    setLocalContacts(supportContacts);
  }, [supportContacts]);

  useEffect(() => {
    const path = location.pathname;
    if (path === "/support-contacts/service" || path.startsWith("/support-contacts/service/")) {
      setActiveTab("Service");
    } else if (path === "/support-contacts" || path === "/support-contacts/staff" || path.startsWith("/support-contacts/staff/")) {
      setActiveTab("Staff");
    }
  }, [location.pathname]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "Service") {
      navigate("/support-contacts/service", { replace: true });
    } else {
      navigate("/support-contacts/staff", { replace: true });
    }
    if (onTabChange) onTabChange(tab);
  };

  const staffCategories = useMemo(() => {
    const set = new Set();
    const list = localStaff.length > 0 ? localStaff : supportStaff;
    for (const s of list) {
      const role = s.Staff_Role ?? s.staff_role ?? "";
      if (role && String(role).trim()) set.add(String(role).trim());
    }
    return Array.from(set).sort();
  }, [localStaff, supportStaff]);

  const serviceCategories = useMemo(() => {
    const set = new Set();
    const list = localContacts.length > 0 ? localContacts : supportContacts;
    for (const c of list) {
      const cat = c.Service_Category ?? c.service_category ?? c.Category ?? c.category ?? "";
      if (cat && String(cat).trim()) set.add(String(cat).trim());
    }
    return Array.from(set).sort();
  }, [localContacts, supportContacts]);

  const handleDeleteStaff = (id) => {
    setLocalStaff((prev) => prev.filter((s) => (s.Staff_Id ?? s.staff_id ?? s.Id ?? s.id) !== id));
  };

  const handleDeleteService = (id) => {
    setLocalContacts((prev) => prev.filter((c) => (c.Id ?? c.Support_Contact_Id ?? c.support_contact_id ?? c.id) !== id));
  };

  return (
    <div className="supportcontacts-container supportcontacts-digix-style">
      <div className="main-card">
        <div className="page-header">
          <div className="page-header-row">
            <div className="tabs-container">
              <button
                className={`tab-button ${activeTab === "Service" ? "active" : ""}`}
                onClick={() => handleTabChange("Service")}
              >
                Service
              </button>
              <button
                className={`tab-button ${activeTab === "Staff" ? "active" : ""}`}
                onClick={() => handleTabChange("Staff")}
              >
                Staff
              </button>
            </div>
          </div>
        </div>

        {activeTab === "Staff" ? (
          <SupportStaff
            staffContacts={localStaff.length > 0 ? localStaff : supportStaff}
            onRefresh={onRefresh}
            onAddContact={onAddContact}
            onViewContact={onViewContact}
            onEditContact={onEditContact}
            onDeleteContact={(id) => {
              handleDeleteStaff(id);
              if (onDeleteContact) onDeleteContact(id);
            }}
            categories={staffCategories}
            rowsPerPage={5}
          />
        ) : (
          <SupportService
            serviceContacts={localContacts.length > 0 ? localContacts : supportContacts}
            onRefresh={onRefresh}
            onAddContact={onAddContact}
            onViewContact={onViewContact}
            onEditContact={(id) => navigate(`/support-contacts/service/${id}/edit`)}
            onDeleteContact={(id) => {
              handleDeleteService(id);
              if (onDeleteContact) onDeleteContact(id);
            }}
            categories={serviceCategories}
            rowsPerPage={5}
          />
        )}
      </div>
    </div>
  );
}

export default SupportContacts;
